/**
 * Colonne latérale (à partir de 900 px) : la marque et son bouton de
 * réduction, la vue (Entreprise ou Ma journée) de qui intervient aussi,
 * « Créer », les rubriques en quatre groupes, l'identité et l'avis sur MyEquip
 * en pied de colonne (§16.2). Sur un écran bas, seules les rubriques défilent. Réduite,
 * elle ne garde que les icônes ; les libellés sont escamotés visuellement,
 * jamais retirés de l'arbre d'accessibilité.
 */
import { Fragment, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { PanelLeftClose } from 'lucide-react';
import { useAuth } from '../../core/auth.js';
import { t } from '../../core/i18n.js';
import { Logo, LogoMark } from '../Logo.js';
import { AccountMenu } from '../AccountMenu.js';
import { areaMatches, type Area, type AreaSection } from '../../app/areas.js';
import type { CreateAction } from '../../app/create-actions.js';
import { CreateMenuButton } from './CreateMenu.js';
import { isSettingsPath, settingsLinkState } from '../../app/settings-overlay.js';
import { WorkModeSwitch } from './WorkModeSwitch.js';
import { ProductFeedbackButton } from './ProductFeedback.js';
import { NavBadge } from '../ui/NavBadge.js';

const NAV_COLLAPSED_KEY = 'myequip.nav.reduite';

/**
 * Réduction de la colonne : la préférence survit au rechargement. Un
 * navigateur qui refuse le stockage local ne casse rien, la préférence est
 * simplement oubliée.
 */
export function useNavCollapsed(): [boolean, (next: boolean) => void] {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(NAV_COLLAPSED_KEY) === '1';
    } catch {
      return false;
    }
  });
  const apply = (next: boolean): void => {
    setCollapsed(next);
    try {
      localStorage.setItem(NAV_COLLAPSED_KEY, next ? '1' : '0');
    } catch {
      // Stockage refusé (mode privé, réglage navigateur) : préférence non mémorisée.
    }
  };
  return [collapsed, apply];
}

function NavEntry({ area, collapsed, active }: { area: Area; collapsed: boolean; active: boolean }): ReactNode {
  const Icon = area.icon;
  const location = useLocation();
  // « Mon entreprise » s'ouvre dans la fenêtre des paramètres, comme depuis le
  // menu du compte (24/10/2026) : une seule façon de régler son entreprise.
  const state = isSettingsPath(area.to) ? settingsLinkState(location) : undefined;
  return (
    <Link to={area.to} state={state} title={collapsed ? area.label : undefined} aria-current={active ? 'page' : undefined}>
      <span className="nav-icon">
        <Icon size={20} strokeWidth={2} aria-hidden="true" />
        <NavBadge count={area.count} label={area.countLabel ?? ''} />
      </span>
      <span className="nav-label">{area.label}</span>
    </Link>
  );
}

export function SideNav(props: {
  sections: readonly AreaSection[];
  actions: readonly CreateAction[];
  collapsed: boolean;
  onCollapsedChange: (next: boolean) => void;
  /** Vue terrain : le scanner de QR code ouvre le menu « Créer ». */
  terrain: boolean;
  onScan: () => void;
}): ReactNode {
  const { me } = useAuth();
  const { pathname } = useLocation();
  // Une seule entrée allumée : la plus précise (une fiche machine est aux Clients, pas à l'Accueil).
  const current = props.sections
    .flatMap((section) => section.areas)
    .filter((area) => areaMatches(area, pathname))
    .sort((left, right) => right.to.length - left.to.length)[0];
  return (
    <nav className="side-nav" aria-label={t('nav.navigationPrincipale')}>
      <div className="side-nav-brand">
        {props.collapsed ? (
          <button
            type="button"
            className="side-nav-brand__mark"
            aria-label={t('nav.deployerMenu')}
            aria-expanded={false}
            title={t('nav.deployerMenu')}
            onClick={() => props.onCollapsedChange(false)}
          >
            <LogoMark size={34} />
          </button>
        ) : (
          <>
            <Logo size={26} />
            <button
              type="button"
              className="side-nav-toggle"
              aria-label={t('nav.reduireMenu')}
              aria-expanded
              title={t('nav.reduireMenu')}
              onClick={() => props.onCollapsedChange(true)}
            >
              <PanelLeftClose size={20} aria-hidden="true" />
            </button>
          </>
        )}
      </div>
      {/* Responsable qui intervient aussi : la vue se voit et se change ici (24/10/2026). */}
      <WorkModeSwitch collapsed={props.collapsed} />
      <CreateMenuButton actions={props.actions} terrain={props.terrain} onScan={props.onScan} />
      {/* Seules les rubriques défilent : marque, « Créer » et identité restent à l'écran. */}
      <div className="side-nav-scroll">
        {props.sections.map((section) => (
          <Fragment key={section.group}>
            {section.title ? <div className="side-nav-section">{section.title}</div> : null}
            {section.areas.map((area) => (
              <NavEntry key={area.to} area={area} collapsed={props.collapsed} active={area === current} />
            ))}
          </Fragment>
        ))}
      </div>
      <div className="side-nav-foot">
        {/* Sur ordinateur, le nom ouvre le menu du compte (11/10/2026) ; à côté,
            l'avis sur MyEquip, comme dans Claude (24/10/2026). */}
        {me ? (
          <>
            <AccountMenu collapsed={props.collapsed} />
            <ProductFeedbackButton />
          </>
        ) : null}
      </div>
    </nav>
  );
}
